import { FlatfileRecord } from '@flatfile/plugin-record-hook';
import { isUSA } from 'helpers/address';
import { firstValidDate, rawDateFromServer } from 'helpers/dates';
import { yup } from 'helpers/yup';
import { DateTime } from 'luxon';
import { CustomField } from 'types/domain';

const MAX_STR_LENGTH = 255;

const MAX_TXT_LENGTH = 65535;

const isTrue = (value: string | null, { explicit }: { explicit: boolean } = { explicit: true }) =>
    explicit
        ? value !== null && ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase())
        : value === null || ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase());

const isFalse = (value: string | null, { explicit }: { explicit: boolean } = { explicit: true }) =>
    explicit
        ? value !== null && ['false', '0', 'no', 'off'].includes(value.trim().toLowerCase())
        : value === null || ['false', '0', 'no', 'off'].includes(value.trim().toLowerCase());

const fixAddress = (record: FlatfileRecord) => {
    const isUnitedStates = isUSA(String(record.get('country')));

    const zipcode = record.get('zipcode') as string | null;
    if (zipcode !== null && zipcode.length < 5 && isUnitedStates) {
        record.set('zipcode', String(zipcode).padStart(5, '0'));
    }
};

interface ListInvalidItemsOptions {
    split?: string;
    locale?: Intl.LocalesArgument;
}

const listInvalidItems =
    (validFn: (value: string) => boolean) =>
    (value: string, { split = ',', locale = 'en' }: ListInvalidItemsOptions = {}) => {
        const listFormatter = new Intl.ListFormat(locale);

        return listFormatter.format(
            value
                .split(split)
                .map(item => item.trim())
                .filter(validFn)
                .map(item => `"${item}"`),
        );
    };

const base = yup.string().trim();
const nullable = base.nullable();
const required = base.required();
const boolean = base.lowercase().oneOf(['true', 'false', '1', '0', 'yes', 'no', 'on', 'off']);
const socialAccount = nullable.max(MAX_STR_LENGTH).test(
    'social-account',
    ({ label }) =>
        `The ${label} must be a valid URL or a username with letters, numbers, dashes, and underscores.`,
    value => {
        if (value === null) {
            return true;
        }

        // no slashes? It's a username.
        if (yup.string().nullable().alphadash().isValidSync(value)) {
            return true;
        }

        // Prepend a schema if it's missing for front-end validation. Will be sent to the
        // back-end without the schema, but the back-end can handle a missing schema.
        let url = '';
        if (
            !((value as string).startsWith('http://') || (value as string).startsWith('https://'))
        ) {
            url = 'http://' + value;
        }

        if (yup.string().nullable().url().isValidSync(url)) {
            return true;
        }

        return false;
    },
);

const dateOfBirth = base.nullable().test(
    'dateOfBirth',
    ({ label }) => `${label} is not a valid date or is not in the past.`,
    value => {
        if (!value?.trim()) {
            return true;
        }

        const now = DateTime.now();
        const longDateYearPrefix = DateTime.fromFormat(value, 'yyyy-MM-dd');
        const longDateYearSuffix = DateTime.fromFormat(value, 'MM/dd/yyyy');
        const shortDateHyphens = DateTime.fromFormat(`${value}-2000`, 'MM-dd-yyyy');
        const shortDateSlashes = DateTime.fromFormat(`${value}/2000`, 'MM/dd/yyyy');

        if (
            !longDateYearPrefix.isValid &&
            !longDateYearSuffix.isValid &&
            !shortDateHyphens.isValid &&
            !shortDateSlashes.isValid
        ) {
            return false;
        }

        if (
            (longDateYearPrefix.isValid && longDateYearPrefix > now) ||
            (longDateYearSuffix.isValid && longDateYearSuffix > now)
        ) {
            return false;
        }

        return true;
    },
);
const customFieldSchema = (field: CustomField) => {
    switch (field.type) {
        case 'number':
            return yup.number().nullable();
        case 'percentage':
            return nullable.matches(/\d+?%/);
        case 'checkbox':
            return boolean.nullable();
        case 'date':
            return nullable.date({ allowTime: false });
        case 'daterange':
            return nullable.test(
                'is-daterange',
                ({ value, label }) => {
                    if (!value) {
                        return true;
                    }

                    const dates = value.split(',');

                    if (dates.length !== 2) {
                        return `${label} is an invalid format: two dates should be separated by a single comma.`;
                    }

                    const start = firstValidDate(dates[0], { allowTime: false });
                    if (!start) {
                        return `${label} is invalid: ${dates[0]} is not a valid date`;
                    }

                    const end = firstValidDate(dates[1], { allowTime: false });
                    if (!end) {
                        return `${label} is invalid: ${dates[1]} is not a valid date`;
                    }

                    if (rawDateFromServer(dates[0]) >= rawDateFromServer(dates[1])) {
                        return `${label} is invalid: the first date (${dates[0]}) must be before the second date (${dates[1]}).`;
                    }
                },
                value => {
                    if (!value) {
                        return true;
                    }

                    const dates = value.split(',');

                    if (dates.length !== 2) {
                        return false;
                    }

                    if (!dates.every(d => yup.string().date({ allowTime: false }).isValidSync(d))) {
                        return false;
                    }

                    return rawDateFromServer(dates[0]) < rawDateFromServer(dates[1]);
                },
            );
        case 'multiselect':
            return nullable.test(
                'is-multiselect',
                ({ value, label }) => {
                    const list = listInvalidItems(item => !field.answers!.includes(item));

                    return `${label} does not contain valid answers: ${list(value, { split: '|' })}`;
                },
                value =>
                    !value ||
                    value
                        .split('|')
                        .map(ans => ans.trim())
                        .every(ans => field.answers!.includes(ans)),
            );
        case 'select':
            return nullable.oneOf(field.answers!);
        case 'text':
            return nullable.max(MAX_STR_LENGTH);
        case 'textarea':
        default:
            return nullable;
    }
};

export {
    fixAddress,
    listInvalidItems,
    isTrue,
    isFalse,
    base,
    required,
    nullable,
    boolean,
    socialAccount,
    dateOfBirth,
    MAX_STR_LENGTH,
    MAX_TXT_LENGTH,
    customFieldSchema,
};
